import type { ComponentChildren } from "preact"
import { useState } from "preact/hooks"
import { ChevronDown, ChevronRight } from "lucide-react"

import { classes } from "@/utils"

interface CollapsibleSectionProps {
  title: string
  /** Rendered next to the title, e.g. the number of rows in the body. */
  count?: number
  description?: ComponentChildren
  /** Rendered on the right of the header, e.g. a filter input. Clicks don't toggle the section. */
  actions?: ComponentChildren
  defaultOpen?: boolean
  /**
   * Tailwind max-height class for the scroll container so a long list never pushes the
   * rest of the page off screen. Pass `undefined` to let the body grow freely.
   */
  bodyMaxHeight?: string
  className?: string
  bodyClassName?: string
  children: ComponentChildren
}

/**
 * A bordered section whose body collapses on click and, while open, scrolls inside a
 * fixed max height instead of running down the page.
 */
export function CollapsibleSection({
  title,
  count,
  description,
  actions,
  defaultOpen = true,
  bodyMaxHeight = "max-h-96",
  className,
  bodyClassName,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <section className={classes("rounded-lg border border-gray-200 bg-white", className)}>
      <div className="flex items-start gap-3 border-b border-gray-200 px-4 py-3">
        <button
          type="button"
          className="flex flex-1 items-start gap-2 text-left"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          )}
          <div>
            <h2 className="font-semibold">
              {title}
              {count !== undefined && <span className="ml-1 font-normal text-gray-500">({count})</span>}
            </h2>
            {description && <p className="mt-1 text-xs text-gray-600">{description}</p>}
          </div>
        </button>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      {open && (
        <div className={classes("overflow-y-auto overflow-x-auto", bodyMaxHeight, bodyClassName)}>{children}</div>
      )}
    </section>
  )
}
